import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { BadgeCheck, Loader2, MapPin, ShieldCheck, Star, Search, Zap } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { TrustBadge } from "@/components/brand/TrustBadge";
import { TrustScore } from "@/components/site/TrustScore";
import { FavoriteButton } from "@/components/site/FavoriteButton";

type Filters = { category?: string; postcode?: string; q?: string; verified?: "any" | "id" | "id_dbs"; instant?: "1"; rtw?: "1"; dbs?: "1"; valid_until?: string };
type Category = { slug: string; name: string };
type HelperRow = {
  id: string;
  display_name: string;
  avatar_url: string | null;
  city: string | null;
  headline: string | null;
  hourly_rate: number | null;
  avg_rating: number;
  rating_count: number;
  jobs_completed: number;
  trust_score: number;
  id_verified: boolean;
  background_check_passed: boolean;
  verification_status: string | null;
  instant_book_enabled: boolean;
  base_postcode: string | null;
  service_radius_miles: number | null;
  response_time_minutes: number | null;
  skills: { slug: string; name: string }[];
  right_to_work_verified?: boolean;
  right_to_work_expires_at?: string | null;
  dbs_verified?: boolean;
  dbs_expires_at?: string | null;
};

export const Route = createFileRoute("/browse")({
  validateSearch: (s) =>
    z.object({
      category: z.string().optional(),
      postcode: z.string().optional(),
      q: z.string().optional(),
      verified: z.enum(["any", "id", "id_dbs"]).optional(),
      instant: z.literal("1").optional(),
      rtw: z.literal("1").optional(),
      dbs: z.literal("1").optional(),
      valid_until: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    }).partial().parse(s),
  head: () => ({
    meta: [
      { title: "Browse verified Helpers in Greater London — Peopels" },
      { name: "description", content: "Find ID-verified, DBS-checked Helpers across every Greater London postcode. Filter by service, verification, and instant book." },
      { property: "og:title", content: "Browse verified Helpers — Peopels" },
      { property: "og:description", content: "ID-verified, DBS-checked local Helpers across Greater London." },
      { property: "og:url", content: "/browse" },
    ],
    links: [{ rel: "canonical", href: "/browse" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Browse verified Helpers — Peopels",
          url: "/browse",
          about: { "@type": "Service", serviceType: "Local home services", areaServed: { "@type": "AdministrativeArea", name: "Greater London" } },
        }),
      },
    ],
  }),
  component: Browse,
});

function Browse() {
  const search = Route.useSearch() as Filters;
  const navigate = useNavigate({ from: "/browse" });
  const [loading, setLoading] = useState(true);
  const [helpers, setHelpers] = useState<HelperRow[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);

  // local state mirrors URL, syncs back on submit/change
  const [q, setQ] = useState(search.q ?? "");
  const [postcode, setPostcode] = useState(search.postcode ?? "");
  const [category, setCategory] = useState(search.category ?? "");
  const [verified, setVerified] = useState<Filters["verified"]>(search.verified ?? "id");
  const [instant, setInstant] = useState<boolean>(search.instant === "1");
  const [rtw, setRtw] = useState<boolean>(search.rtw === "1");
  const [dbs, setDbs] = useState<boolean>(search.dbs === "1");
  const [validUntil, setValidUntil] = useState<string>(search.valid_until ?? "");

  useEffect(() => {
    supabase.from("categories").select("slug, name").eq("active", true).order("name")
      .then(({ data }) => setCategories((data ?? []) as Category[]));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      let qb = supabase
        .from("helper_profiles")
        .select(`
          id, headline, hourly_rate, base_postcode, service_radius_miles,
          avg_rating, rating_count, jobs_completed, trust_score,
          id_verified, background_check_passed, verification_status,
          instant_book_enabled, available, suspended, response_time_minutes,
          right_to_work_verified, right_to_work_expires_at, dbs_verified, dbs_expires_at,
          profile:profiles!helper_profiles_id_fkey(full_name, display_name, avatar_url, city),
          skills:helper_skills(category:categories(slug, name))
        `)
        .eq("suspended", false)
        .eq("available", true)
        .order("trust_score", { ascending: false })
        .order("avg_rating", { ascending: false })
        .limit(60);

      if (search.verified === "id" || !search.verified) qb = qb.eq("id_verified", true);
      if (search.verified === "id_dbs") qb = qb.eq("id_verified", true).eq("background_check_passed", true);
      if (search.instant === "1") qb = qb.eq("instant_book_enabled", true);
      if (search.rtw === "1") qb = qb.eq("right_to_work_verified", true);
      if (search.dbs === "1") qb = qb.eq("dbs_verified", true);
      if (search.valid_until) {
        const iso = new Date(search.valid_until + "T00:00:00Z").toISOString();
        if (search.rtw === "1") qb = qb.gte("right_to_work_expires_at", iso);
        if (search.dbs === "1") qb = qb.gte("dbs_expires_at", iso);
      }
      if (search.postcode) {
        const prefix = search.postcode.trim().toUpperCase().split(" ")[0];
        if (prefix) qb = qb.ilike("base_postcode", `${prefix}%`);
      }

      const { data, error } = await qb;
      if (cancelled) return;
      if (error || !data) { setHelpers([]); setLoading(false); return; }

      let rows: HelperRow[] = (data as any[]).map((h) => ({
        id: h.id,
        display_name: h.profile?.display_name || h.profile?.full_name || "Helper",
        avatar_url: h.profile?.avatar_url ?? null,
        city: h.profile?.city ?? null,
        headline: h.headline,
        hourly_rate: h.hourly_rate != null ? Number(h.hourly_rate) : null,
        avg_rating: Number(h.avg_rating ?? 0),
        rating_count: Number(h.rating_count ?? 0),
        jobs_completed: Number(h.jobs_completed ?? 0),
        trust_score: Number(h.trust_score ?? 0),
        id_verified: !!h.id_verified,
        background_check_passed: !!h.background_check_passed,
        verification_status: h.verification_status,
        instant_book_enabled: !!h.instant_book_enabled,
        base_postcode: h.base_postcode,
        service_radius_miles: h.service_radius_miles,
        response_time_minutes: h.response_time_minutes,
        right_to_work_verified: !!h.right_to_work_verified,
        right_to_work_expires_at: h.right_to_work_expires_at,
        dbs_verified: !!h.dbs_verified,
        dbs_expires_at: h.dbs_expires_at,
        skills: ((h.skills ?? []) as any[]).map((s) => s.category).filter(Boolean),
      }));

      if (search.category) rows = rows.filter((r) => r.skills.some((s) => s.slug === search.category));
      if (search.q) {
        const needle = search.q.toLowerCase();
        rows = rows.filter((r) =>
          r.display_name.toLowerCase().includes(needle) ||
          (r.headline ?? "").toLowerCase().includes(needle) ||
          r.skills.some((s) => s.name.toLowerCase().includes(needle)),
        );
      }
      setHelpers(rows);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [search.category, search.postcode, search.q, search.verified, search.instant, search.rtw, search.dbs, search.valid_until]);

  function apply(e?: React.FormEvent) {
    e?.preventDefault();
    navigate({
      search: () => ({
        q: q.trim() || undefined,
        postcode: postcode.trim() || undefined,
        category: category || undefined,
        verified,
        instant: instant ? "1" : undefined,
        rtw: rtw ? "1" : undefined,
        dbs: dbs ? "1" : undefined,
        valid_until: validUntil || undefined,
      }),
    });
  }

  const verifiedCount = useMemo(() => helpers.filter((h) => h.id_verified).length, [helpers]);

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main id="main-content" className="mx-auto w-full max-w-7xl flex-1 px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold sm:text-3xl">Find a verified Helper in London</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Every Helper listed has passed ID verification. Filter to see DBS-checked or instant-book Helpers.
            </p>
          </div>
        </div>

        {/* Filters */}
        <form onSubmit={apply} className="mt-6 grid gap-3 rounded-2xl border border-border bg-card p-4 shadow-card sm:p-5 md:grid-cols-[1.5fr_1fr_1fr_auto]">
          <div>
            <Label htmlFor="q" className="text-xs font-medium">Search</Label>
            <div className="relative mt-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input id="q" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, skill or keyword" className="pl-9" />
            </div>
          </div>
          <div>
            <Label htmlFor="cat" className="text-xs font-medium">Service</Label>
            <select
              id="cat" value={category} onChange={(e) => setCategory(e.target.value)}
              className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">All services</option>
              {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <Label htmlFor="pc" className="text-xs font-medium">Postcode</Label>
            <Input id="pc" value={postcode} onChange={(e) => setPostcode(e.target.value)} placeholder="e.g. SW1A" maxLength={8} className="mt-1 uppercase" />
          </div>
          <div className="flex items-end">
            <Button type="submit" className="h-10 w-full md:w-auto">Apply filters</Button>
          </div>

          <div className="md:col-span-4 flex flex-wrap items-center gap-2 pt-1">
            <span className="text-xs font-medium text-muted-foreground">Verification:</span>
            {([
              { v: "any", label: "All Helpers" },
              { v: "id", label: "ID verified" },
              { v: "id_dbs", label: "ID + DBS" },
            ] as const).map((opt) => (
              <button
                key={opt.v}
                type="button"
                onClick={() => { setVerified(opt.v); navigate({ search: (s: any) => ({ ...s, verified: opt.v }) }); }}
                className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium transition ${
                  (search.verified ?? "id") === opt.v
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border bg-background text-muted-foreground hover:border-primary/40"
                }`}
              >
                {opt.v !== "any" && <ShieldCheck className="h-3 w-3" />} {opt.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => { const next = !instant; setInstant(next); navigate({ search: (s: any) => ({ ...s, instant: next ? "1" : undefined }) }); }}
              className={`ml-2 inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium transition ${
                search.instant === "1"
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border bg-background text-muted-foreground hover:border-primary/40"
              }`}
            >
              <Zap className="h-3 w-3" /> Instant book
            </button>
            <button
              type="button"
              onClick={() => { const next = !rtw; setRtw(next); navigate({ search: (s: any) => ({ ...s, rtw: next ? "1" : undefined }) }); }}
              className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium transition ${search.rtw === "1" ? "border-primary bg-primary/10 text-primary" : "border-border bg-background text-muted-foreground hover:border-primary/40"}`}
            >
              <ShieldCheck className="h-3 w-3" /> Right to work
            </button>
            <button
              type="button"
              onClick={() => { const next = !dbs; setDbs(next); navigate({ search: (s: any) => ({ ...s, dbs: next ? "1" : undefined }) }); }}
              className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium transition ${search.dbs === "1" ? "border-primary bg-primary/10 text-primary" : "border-border bg-background text-muted-foreground hover:border-primary/40"}`}
            >
              <ShieldCheck className="h-3 w-3" /> DBS checked
            </button>
            {(rtw || dbs) && (
              <label className="ml-2 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                Valid until
                <input
                  type="date"
                  value={validUntil}
                  onChange={(e) => { setValidUntil(e.target.value); navigate({ search: (s: any) => ({ ...s, valid_until: e.target.value || undefined }) }); }}
                  className="h-7 rounded-md border border-input bg-background px-2 text-xs"
                />
              </label>
            )}
          </div>
        </form>

        {/* Results */}
        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {loading ? "Searching…" : `${helpers.length} Helper${helpers.length === 1 ? "" : "s"}${verifiedCount === helpers.length && helpers.length > 0 ? " · all ID verified" : ""}`}
          </p>
        </div>

        {loading ? (
          <div className="mt-10 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : helpers.length === 0 ? (
          <EmptyState />
        ) : (
          <ul className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {helpers.map((h) => <HelperCard key={h.id} h={h} />)}
          </ul>
        )}
      </main>
      <Footer />
    </div>
  );
}

function HelperCard({ h }: { h: HelperRow }) {
  return (
    <li className="relative">
      <div className="absolute right-3 top-3 z-10">
        <FavoriteButton helperId={h.id} helperName={h.display_name} />
      </div>
      <Link
        to="/helpers/$helperId"
        params={{ helperId: h.id }}
        className="group flex h-full flex-col rounded-2xl border border-border bg-card p-4 shadow-card transition-all hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-elegant"
      >
        <div className="flex items-start gap-3">
          <div className="relative shrink-0">
            {h.avatar_url ? (
              <img src={h.avatar_url} alt="" loading="lazy" width={56} height={56} className="h-14 w-14 rounded-full object-cover ring-2 ring-background" />
            ) : (
              <span className="grid h-14 w-14 place-items-center rounded-full bg-accent text-base font-semibold text-primary ring-2 ring-background">
                {h.display_name.slice(0, 1)}
              </span>
            )}
            {h.id_verified && (
              <span
                className="absolute -bottom-1 -right-1 grid h-5 w-5 place-items-center rounded-full bg-emerald-500 text-white ring-2 ring-background"
                aria-label="ID verified"
                title="ID verified"
              >
                <BadgeCheck className="h-3 w-3" />
              </span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <p className="truncate font-semibold">{h.display_name}</p>
              {h.rating_count > 0 && (
                <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-amber-600">
                  <Star className="h-3 w-3 fill-current" /> {h.avg_rating.toFixed(2)}
                </span>
              )}
            </div>
            <p className="truncate text-xs text-muted-foreground">{h.headline ?? "Verified local Helper"}</p>
            <div className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground">
              <MapPin className="h-3 w-3" /> {h.city ?? h.base_postcode ?? "London"}
              {h.service_radius_miles ? <span> · {h.service_radius_miles} mi radius</span> : null}
            </div>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-1.5">
          {h.id_verified && h.background_check_passed && <TrustBadge variant="trusted-professional" />}
          {h.id_verified && !h.background_check_passed && <TrustBadge variant="id-verified" />}
          {h.background_check_passed && <TrustBadge variant="verified-professional" />}
          {h.avg_rating >= 4.8 && h.jobs_completed >= 20 && <TrustBadge variant="top-rated" />}
          {h.instant_book_enabled && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-[11px] font-semibold text-amber-700 dark:text-amber-300">
              <Zap className="h-3 w-3" /> Instant book
            </span>
          )}
        </div>

        {h.skills.length > 0 && (
          <p className="mt-2 truncate text-xs text-muted-foreground">
            {h.skills.slice(0, 4).map((s) => s.name).join(" · ")}
          </p>
        )}

        <div className="mt-3 flex items-center justify-between border-t border-border pt-3">
          <TrustScore score={h.trust_score} size="sm" />
          <div className="text-right">
            {h.hourly_rate != null && (
              <p className="text-sm font-semibold">£{h.hourly_rate.toFixed(0)}<span className="text-xs font-normal text-muted-foreground">/hr</span></p>
            )}
            <p className="text-[11px] text-muted-foreground">
              {h.jobs_completed > 0 ? `${h.jobs_completed} job${h.jobs_completed === 1 ? "" : "s"}` : "New Helper"}
              {h.rating_count > 0 ? ` · ${h.rating_count} review${h.rating_count === 1 ? "" : "s"}` : ""}
            </p>
          </div>
        </div>
      </Link>
    </li>
  );
}

function EmptyState() {
  return (
    <div className="mt-10 rounded-2xl border border-dashed border-border bg-card p-10 text-center">
      <ShieldCheck className="mx-auto h-8 w-8 text-muted-foreground" />
      <p className="mt-3 font-semibold">No verified Helpers match those filters yet</p>
      <p className="mt-1 text-sm text-muted-foreground">Try a wider postcode or remove a filter.</p>
      <div className="mt-4 flex justify-center gap-2">
        <Link to="/browse" search={{ verified: "id" }} className="rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-accent">Reset filters</Link>
        <Link to="/post-job" className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90">Post a job instead</Link>
      </div>
    </div>
  );
}
